import React, { useEffect, useMemo, useRef, useState } from "react";
import { Button, Col, Collapse, Form, Row } from "react-bootstrap";
import { FaPlus, FaTrash } from "react-icons/fa";

function parseOpcoes(opcoes) {
  const str = (opcoes || "").toString();
  const lines = str.split("\n");

  const rows = lines
    .map((line) => line.trim())
    .filter(Boolean)
    .map((line) => {
      const idxColon = line.indexOf(":");
      const idxSemicolon = line.indexOf(";");

      let idx = -1;
      if (idxColon === -1) idx = idxSemicolon;
      else if (idxSemicolon === -1) idx = idxColon;
      else idx = Math.min(idxColon, idxSemicolon);

      if (idx === -1) {
        const key = line.trim();
        return { key, label: key };
      }

      const key = line.slice(0, idx).trim();
      const label = line.slice(idx + 1).trim();
      return { key, label: label || key };
    });

  return rows;
}

function serializeOpcoes(rows) {
  return (rows || [])
    .map((r) => ({
      key: (r?.key || "").toString().trim(),
      label: (r?.label || "").toString().trim(),
    }))
    .filter((r) => r.key)
    .map((r) => `${r.key}:${r.label || r.key}`)
    .join("\n");
}

function buildErrors(rows) {
  const keyCount = new Map();
  for (const r of rows) {
    const k = (r?.key || "").toString().trim();
    if (!k) continue;
    keyCount.set(k, (keyCount.get(k) || 0) + 1);
  }

  return rows.map((r) => {
    const key = (r?.key || "").toString();
    const label = (r?.label || "").toString();
    const keyTrim = key.trim();
    const labelTrim = label.trim();

    const keyErrors = [];
    const labelErrors = [];

    if (!keyTrim) keyErrors.push("Chave obrigatoria");
    if (keyTrim.includes(":")) keyErrors.push('Nao pode conter ":"');
    if (keyTrim.includes(";")) keyErrors.push('Nao pode conter ";"');
    if (keyTrim.includes("\n"))
      keyErrors.push("Nao pode conter quebra de linha");
    if (keyTrim.includes(",")) keyErrors.push("Nao pode conter virgula");
    if (keyTrim && (keyCount.get(keyTrim) || 0) > 1)
      keyErrors.push("Chave duplicada");

    if (!labelTrim) labelErrors.push("Texto obrigatorio");
    if (labelTrim.includes(":")) labelErrors.push('Nao pode conter ":"');
    if (labelTrim.includes(";")) labelErrors.push('Nao pode conter ";"');
    if (labelTrim.includes("\n"))
      labelErrors.push("Nao pode conter quebra de linha");

    return { keyErrors, labelErrors };
  });
}

export default function OpcoesCheckboxEditor({ value, onChange }) {
  const lastEmittedRef = useRef(null);
  const [showRaw, setShowRaw] = useState(false);
  const [rows, setRows] = useState(() => parseOpcoes(value));

  const safeOnChange = typeof onChange === "function" ? onChange : () => {};

  useEffect(() => {
    const next = (value || "").toString();
    if (lastEmittedRef.current === next) return;
    setRows(parseOpcoes(next));
  }, [value]);

  const errors = useMemo(() => buildErrors(rows), [rows]);
  const hasAnyError = useMemo(
    () => errors.some((e) => e.keyErrors.length || e.labelErrors.length),
    [errors]
  );

  const emit = (nextRows) => {
    const serialized = serializeOpcoes(nextRows);
    lastEmittedRef.current = serialized;
    safeOnChange(serialized);
  };

  const updateRow = (idx, patch) => {
    setRows((prev) => {
      const next = prev.length ? [...prev] : [];
      next[idx] = { ...next[idx], ...patch };
      emit(next);
      return next;
    });
  };

  const addRow = () => {
    setRows((prev) => {
      const next = [...(prev || []), { key: "", label: "" }];
      emit(next);
      return next;
    });
  };

  const removeRow = (idx) => {
    setRows((prev) => {
      const next = (prev || []).filter((_, i) => i !== idx);
      emit(next);
      return next;
    });
  };

  return (
    <>
      <div className="border rounded bg-light p-3">
        <div className="d-flex flex-column flex-md-row justify-content-between align-items-start align-items-md-center gap-2 mb-3">
          <div>
            <div className="fw-semibold">Opções (chave:valor)</div>
            <div className="text-muted" style={{ fontSize: "0.9em" }}>
              A chave é o identificador salvo; o valor é o texto exibido.
            </div>
          </div>
          <div className="d-flex gap-2">
            <Button variant="secondary" size="sm" onClick={addRow}>
              <FaPlus className="me-1" /> Adicionar opção
            </Button>
            <Button
              variant="outline-secondary"
              size="sm"
              onClick={() => setShowRaw((s) => !s)}
            >
              {showRaw ? "Fechar modo texto" : "Modo texto"}
            </Button>
          </div>
        </div>

        {rows.length === 0 ? (
          <div className="text-muted" style={{ fontSize: "0.9em" }}>
            Nenhuma opção adicionada.
          </div>
        ) : null}

        <div
          className="border rounded bg-white p-2"
          style={{ overflowX: "auto" }}
        >
          <div style={{ minWidth: 880 }}>
            {rows.map((r, idx) => {
              const e = errors[idx] || { keyErrors: [], labelErrors: [] };
              const keyInvalid = e.keyErrors.length > 0;
              const labelInvalid = e.labelErrors.length > 0;

              return (
                <div
                  className="border rounded bg-light p-2 mb-2"
                  key={`opt_${idx}`}
                >
                  <div className="d-flex justify-content-between align-items-center mb-2">
                    <div className="text-muted" style={{ fontSize: "0.9em" }}>
                      Opção {idx + 1}
                    </div>
                    <Button
                      variant="outline-danger"
                      size="sm"
                      onClick={() => removeRow(idx)}
                      title="Remover opção"
                    >
                      <FaTrash className="me-1" /> Remover
                    </Button>
                  </div>

                  <Row className="g-2 align-items-start">
                    <Col xs={12} md={4}>
                      <Form.Label className="mb-1" style={{ fontSize: "0.9em" }}>
                        Chave
                      </Form.Label>
                      <Form.Control
                        type="text"
                        value={r.key}
                        placeholder="Ex: consentimento_compartilhamento"
                        isInvalid={keyInvalid}
                        onChange={(ev) => updateRow(idx, { key: ev.target.value })}
                      />
                      {keyInvalid ? (
                        <div className="invalid-feedback d-block">
                          {e.keyErrors.join(" | ")}
                        </div>
                      ) : null}
                    </Col>

                    <Col xs={12} md={7}>
                      <Form.Label className="mb-1" style={{ fontSize: "0.9em" }}>
                        Texto exibido
                      </Form.Label>
                      <Form.Control
                        as="textarea"
                        rows={3}
                        style={{ minHeight: 72 }}
                        value={r.label}
                        placeholder="Ex: Sim, autorizo."
                        isInvalid={labelInvalid}
                        onChange={(ev) =>
                          updateRow(idx, { label: ev.target.value })
                        }
                      />
                      {labelInvalid ? (
                        <div className="invalid-feedback d-block">
                          {e.labelErrors.join(" | ")}
                        </div>
                      ) : null}
                    </Col>

                  </Row>
                </div>
              );
            })}
          </div>
        </div>

        {rows.length > 0 ? (
          <div className="mt-2 text-muted" style={{ fontSize: "0.88em" }}>
            Pre-visualizacao: {rows.map((r) => `(${r.label || r.key})`).join(" ")}
          </div>
        ) : null}

        <Collapse in={showRaw}>
          <div className="mt-3">
            <Form.Label className="mb-1" style={{ fontSize: "0.9em" }}>
              Edicao em texto (1 por linha)
            </Form.Label>
            <Form.Control
              as="textarea"
              rows={4}
              value={(value || "").toString()}
              onChange={(e) => {
                const next = e.target.value;
                lastEmittedRef.current = next;
                safeOnChange(next);
                setRows(parseOpcoes(next));
              }}
              placeholder={
                "ex: declaracao_1:Declaro que...\nex: declaracao_2:Declaro que..."
              }
            />
            <Form.Text className="text-muted" style={{ fontSize: "0.9em" }}>
              Regras: chave única; sem <code>:</code>, <code>;</code>, vírgula ou quebras de
              linha.
            </Form.Text>
          </div>
        </Collapse>

        {hasAnyError ? (
          <div className="text-danger mt-2" style={{ fontSize: "0.9em" }}>
            Corrija as opções inválidas antes de salvar.
          </div>
        ) : null}
      </div>
    </>
  );
}
